import { useEffect, useState, useCallback } from 'react'
import { TimePeriodFilter, filterTradesByPeriod, getTimePeriodRange } from '@/components/shared/TimePeriodFilter'
import { ACCOUNTS as ACCOUNT_LIST } from '@/lib/constants'
import { supabase } from '@/lib/supabase'
import ExportDropdown from '../components/portfolio/ExportDropdown'
import type { Position } from '@/types'

interface AccountSummary {
  slug: string
  name: string
  optionsPnL: number
  shareAppreciation: number
  totalPnL: number
}

const ACCOUNTS = ACCOUNT_LIST.map(a => ({ slug: a.slug, name: a.name }))

const PORTFOLIO_ORDER_KEY = 'tradingApp_portfolioOrder'
const PORTFOLIO_HIDDEN_KEY = 'tradingApp_portfolioHidden'

function getSavedOrder(): string[] {
  try {
    const stored = localStorage.getItem(PORTFOLIO_ORDER_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as string[]
      const allSlugs = ACCOUNTS.map(a => a.slug)
      const valid = parsed.filter(s => allSlugs.includes(s))
      const missing = allSlugs.filter(s => !parsed.includes(s))
      return [...valid, ...missing]
    }
  } catch {}
  return ACCOUNTS.map(a => a.slug)
}

function getSavedHidden(): string[] {
  try {
    const stored = localStorage.getItem(PORTFOLIO_HIDDEN_KEY)
    return stored ? JSON.parse(stored) : []
  } catch { return [] }
}

export function PortfolioPage() {
  const [loading, setLoading] = useState(true)
  const [timePeriod, setTimePeriod] = useState<string>('all')
  const [allTradesData, setAllTradesData] = useState<Record<string, any[]>>({})
  const [allPositionsData, setAllPositionsData] = useState<Position[]>([])
  const [accountOrder, setAccountOrder] = useState<string[]>(getSavedOrder)
  const [hiddenAccounts, setHiddenAccounts] = useState<string[]>(getSavedHidden)
  const [showHidden, setShowHidden] = useState(false)
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null)

  const saveOrder = useCallback((newOrder: string[]) => {
    setAccountOrder(newOrder)
    localStorage.setItem(PORTFOLIO_ORDER_KEY, JSON.stringify(newOrder))
  }, [])

  const hideAccount = useCallback((slug: string) => {
    const updated = [...hiddenAccounts, slug]
    setHiddenAccounts(updated)
    localStorage.setItem(PORTFOLIO_HIDDEN_KEY, JSON.stringify(updated))
  }, [hiddenAccounts])

  const showAccount = useCallback((slug: string) => {
    const updated = hiddenAccounts.filter(s => s !== slug)
    setHiddenAccounts(updated)
    localStorage.setItem(PORTFOLIO_HIDDEN_KEY, JSON.stringify(updated))
  }, [hiddenAccounts])

  const handleRowDragStart = (index: number) => (e: React.DragEvent) => {
    setDragIdx(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleRowDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault()
    setDragOverIdx(index)
  }

  const handleRowDrop = (targetIndex: number) => (e: React.DragEvent) => {
    e.preventDefault()
    if (dragIdx === null || dragIdx === targetIndex) {
      setDragIdx(null)
      setDragOverIdx(null)
      return
    }
    const newOrder = [...accountOrder]
    const [moved] = newOrder.splice(dragIdx, 1)
    newOrder.splice(targetIndex, 0, moved)
    saveOrder(newOrder)
    setDragIdx(null)
    setDragOverIdx(null)
  }

  useEffect(() => {
    loadPortfolioData()
  }, [])

  // Filter trades by time period using shared utility
  const filterByPeriod = (trades: any[]): any[] => {
    return filterTradesByPeriod(trades, timePeriod)
  }

  const loadPortfolioData = async () => {
    setLoading(true)
    try {
      const tradesMap: Record<string, any[]> = {}
      const allPositions: Position[] = []

      await Promise.all(
        ACCOUNTS.map(async (account) => {
          try {
            const [tradesResult, positionsResult] = await Promise.all([
              supabase.from('trades').select('*').eq('account_slug', account.slug),
              supabase.from('positions').select('*').eq('account_slug', account.slug),
            ])
            tradesMap[account.slug] = tradesResult.data || []
            const positions: Position[] = positionsResult.data || []
            // Keep ALL positions (open + closed). Open ones feed the position summary;
            // closed ones (sold via Sell button) contribute share appreciation to P&L.
            allPositions.push(...positions)
          } catch {
            tradesMap[account.slug] = []
          }
        })
      )

      setAllTradesData(tradesMap)
      setAllPositionsData(allPositions)
    } catch (error) {
      console.error('Failed to load portfolio data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Resolve time period range once (for filtering position sales by soldDate)
  const periodRange = getTimePeriodRange(timePeriod)

  // Compute summaries based on current time period filter, respecting custom order
  const allSummaries: Record<string, AccountSummary> = {}
  ACCOUNTS.forEach(account => {
    const trades = filterByPeriod(allTradesData[account.slug] || [])
    const optionsPnL = trades
      .filter((t: any) => t.status === 'closed' || t.status !== 'open')
      .reduce((sum: number, t: any) => sum + (t.net_position || t.netPosition || 0), 0)

    // Share appreciation from trades closed with the old "Sold (Shares)" action
    const tradeShareAppreciation = trades
      .filter((t: any) => t.share_appreciation || t.shareAppreciation)
      .reduce((sum: number, t: any) => sum + (t.share_appreciation || t.shareAppreciation || 0), 0)

    // Share appreciation from CLOSED positions (sold via the Sell button)
    const positionShareAppreciation = allPositionsData
      .filter((p: any) => {
        if (p.account_slug !== account.slug) return false
        if (p.status !== 'closed') return false
        const soldPrice = p.sold_price ?? p.soldPrice
        const soldDate = p.sold_date ?? p.soldDate
        if (!soldPrice || !soldDate) return false
        // Time period filter on sold date
        if (periodRange) {
          if (soldDate < periodRange.start || soldDate > periodRange.end) return false
        }
        return true
      })
      .reduce((sum: number, p: any) => {
        const soldPrice = p.sold_price ?? p.soldPrice ?? 0
        const costBasis = p.cost_basis ?? p.costBasis ?? 0
        const sharesSold = p.shares_sold ?? p.sharesSold ?? 0
        return sum + ((soldPrice - costBasis) * sharesSold)
      }, 0)

    const shareAppreciation = tradeShareAppreciation + positionShareAppreciation

    allSummaries[account.slug] = {
      slug: account.slug,
      name: account.name,
      optionsPnL: Math.round(optionsPnL * 100) / 100,
      shareAppreciation: Math.round(shareAppreciation * 100) / 100,
      totalPnL: Math.round((optionsPnL + shareAppreciation) * 100) / 100,
    }
  })

  // Visible summaries in custom order
  const visibleSummaries = accountOrder
    .filter(slug => !hiddenAccounts.includes(slug) && allSummaries[slug])
    .map(slug => allSummaries[slug])
    .filter(a => a.totalPnL !== 0 || timePeriod === 'all')

  // Hidden summaries for the "show hidden" panel
  const hiddenSummaries = accountOrder
    .filter(slug => hiddenAccounts.includes(slug) && allSummaries[slug])
    .map(slug => allSummaries[slug])

  if (loading) {
    return <div className="p-6 text-[var(--color-text-muted)]">Loading portfolio...</div>
  }

  const totalOptionsPnL = visibleSummaries.reduce((sum, a) => sum + a.optionsPnL, 0)
  const totalShareAppreciation = visibleSummaries.reduce((sum, a) => sum + a.shareAppreciation, 0)
  const grandTotalPnL = totalOptionsPnL + totalShareAppreciation

  // Position summary — open positions only
  const openPositionsData = allPositionsData.filter((p: any) => p.status === 'open')
  const totalShares = openPositionsData.reduce((sum, p) => sum + (p.quantity ?? 0), 0)
  const totalCostBasis = openPositionsData.reduce((sum, p) => sum + ((p.quantity ?? 0) * ((p as any).cost_basis ?? p.costBasis ?? 0)), 0)

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-[var(--color-text-primary)]">Portfolio Overview</h1>
        <div className="flex items-center gap-3">
          <ExportDropdown />
          <TimePeriodFilter value={timePeriod} onChange={setTimePeriod} />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="border border-[var(--color-border)] rounded-lg p-4">
          <p className="text-xs text-[var(--color-text-muted)] uppercase">Total P&L</p>
          <p className={`text-2xl font-bold font-mono ${grandTotalPnL >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>
            ${grandTotalPnL.toFixed(2)}
          </p>
        </div>
        <div className="border border-[var(--color-border)] rounded-lg p-4">
          <p className="text-xs text-[var(--color-text-muted)] uppercase">Options Premium</p>
          <p className={`text-2xl font-bold font-mono ${totalOptionsPnL >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>
            ${totalOptionsPnL.toFixed(2)}
          </p>
        </div>
        <div className="border border-[var(--color-border)] rounded-lg p-4">
          <p className="text-xs text-[var(--color-text-muted)] uppercase">Share Appreciation</p>
          <p className={`text-2xl font-bold font-mono ${totalShareAppreciation >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>
            ${totalShareAppreciation.toFixed(2)}
          </p>
        </div>
        <div className="border border-[var(--color-border)] rounded-lg p-4">
          <p className="text-xs text-[var(--color-text-muted)] uppercase">Shares Held</p>
          <p className="text-2xl font-bold font-mono text-[var(--color-text-primary)]">
            {totalShares}
          </p>
        </div>
      </div>

      {/* Account P&L table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-[var(--color-text-muted)] uppercase tracking-wide">
            Account P&L Breakdown
          </h2>
          {hiddenSummaries.length > 0 && (
            <button
              onClick={() => setShowHidden(!showHidden)}
              className="text-xs px-3 py-1.5 rounded border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-accent)] transition-colors"
            >
              {showHidden ? 'Hide' : 'Show'} hidden ({hiddenSummaries.length})
            </button>
          )}
        </div>
        <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg-tertiary)]">
              <tr>
                <th className="px-2 py-2.5 w-8"></th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Account</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-[var(--color-text-muted)] uppercase">Options P&L</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-[var(--color-text-muted)] uppercase">Share Appreciation</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-[var(--color-text-muted)] uppercase">Total P&L</th>
                <th className="px-2 py-2.5 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {visibleSummaries.map((account, index) => (
                <tr
                  key={account.slug}
                  className={`border-t border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]/50 group ${
                    dragOverIdx === index && dragIdx !== null && dragIdx !== index ? 'border-t-2 border-t-[var(--color-accent)]' : ''
                  }`}
                  draggable
                  onDragStart={handleRowDragStart(index)}
                  onDragOver={handleRowDragOver(index)}
                  onDragEnter={(e) => e.preventDefault()}
                  onDrop={handleRowDrop(index)}
                  onDragEnd={() => { setDragIdx(null); setDragOverIdx(null) }}
                  style={{ opacity: dragIdx === index ? 0.4 : 1 }}
                >
                  <td className="px-2 py-2.5 cursor-grab text-[var(--color-text-muted)] text-center">⋮⋮</td>
                  <td className="px-4 py-2.5 font-medium text-[var(--color-text-primary)]">{account.name}</td>
                  <td className={`px-4 py-2.5 text-right font-mono ${account.optionsPnL >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>
                    ${account.optionsPnL.toFixed(2)}
                  </td>
                  <td className={`px-4 py-2.5 text-right font-mono ${account.shareAppreciation >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>
                    {account.shareAppreciation !== 0 ? `$${account.shareAppreciation.toFixed(2)}` : '—'}
                  </td>
                  <td className={`px-4 py-2.5 text-right font-mono font-medium ${account.totalPnL >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>
                    ${account.totalPnL.toFixed(2)}
                  </td>
                  <td className="px-2 py-2.5">
                    <button
                      onClick={() => hideAccount(account.slug)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-[var(--color-text-muted)] hover:text-[var(--color-red)] text-xs"
                      title="Hide this account"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[var(--color-bg-tertiary)] border-t border-[var(--color-border)]">
              <tr>
                <td className="px-2 py-2.5"></td>
                <td className="px-4 py-2.5 font-bold text-[var(--color-text-primary)]">Total</td>
                <td className={`px-4 py-2.5 text-right font-mono font-bold ${totalOptionsPnL >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>
                  ${totalOptionsPnL.toFixed(2)}
                </td>
                <td className={`px-4 py-2.5 text-right font-mono font-bold ${totalShareAppreciation >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>
                  {totalShareAppreciation !== 0 ? `$${totalShareAppreciation.toFixed(2)}` : '—'}
                </td>
                <td className={`px-4 py-2.5 text-right font-mono font-bold ${grandTotalPnL >= 0 ? 'text-[var(--color-green)]' : 'text-[var(--color-red)]'}`}>
                  ${grandTotalPnL.toFixed(2)}
                </td>
                <td className="px-2 py-2.5"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Hidden accounts panel */}
        {showHidden && hiddenSummaries.length > 0 && (
          <div className="mt-3 border border-dashed border-[var(--color-border)] rounded-lg p-3">
            <p className="text-xs text-[var(--color-text-muted)] mb-2">Hidden accounts (click + to restore):</p>
            <div className="flex flex-wrap gap-2">
              {hiddenSummaries.map(account => (
                <button
                  key={account.slug}
                  onClick={() => showAccount(account.slug)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] transition-colors"
                >
                  <span>+</span> {account.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Share Positions summary */}
      {openPositionsData.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-[var(--color-text-muted)] uppercase tracking-wide mb-3">
            Share Positions (All Accounts)
          </h2>
          <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-bg-tertiary)]">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Ticker</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Account</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-[var(--color-text-muted)] uppercase">Shares</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-[var(--color-text-muted)] uppercase">Cost Basis</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-[var(--color-text-muted)] uppercase">Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {openPositionsData.map(pos => (
                  <tr key={pos.id} className="border-t border-[var(--color-border)]">
                    <td className="px-4 py-2.5 font-medium text-[var(--color-accent)]">{pos.ticker}</td>
                    <td className="px-4 py-2.5 text-[var(--color-text-secondary)]">{(pos as any).account_slug || pos.accountSlug || ''}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{pos.quantity ?? 0}</td>
                    <td className="px-4 py-2.5 text-right font-mono">${((pos as any).cost_basis ?? pos.costBasis ?? 0).toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right font-mono">${((pos.quantity ?? 0) * ((pos as any).cost_basis ?? pos.costBasis ?? 0)).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[var(--color-bg-tertiary)] border-t border-[var(--color-border)]">
                <tr>
                  <td className="px-4 py-2.5 font-bold" colSpan={2}>Total</td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold">{totalShares}</td>
                  <td className="px-4 py-2.5 text-right font-mono">—</td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold">${totalCostBasis.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
